/**
 * Matcher control-plane client (A7). Helper availability is PRIVATE: it is signed
 * but NEVER published to the public firehose (that would leak who is willing to
 * help, and where). Instead the signed DAG-CBOR envelope is POSTed straight to the
 * matcher's `/availability` endpoint, which verifies the signature against the
 * envelope's own signingKey (services/matcher/server.go). Mirrors RelayTransport's
 * octet-stream POST shape so the two control planes stay symmetric.
 */
import { sseEvents } from "@beacon/transport/relay";
export interface MatcherClientOptions {
  baseUrl: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export interface AvailabilityAck {
  registered: boolean;
  identityId?: string;
}

export interface MatchSuggestion {
  subject: string;
  need: string;
  urgency: number;
  rank: number;
  cells: string[];
  updatedAt: string;
}

const NEED_TYPES = new Set(["medical", "rescue", "transport", "shelter", "supplies", "hands", "other"]);

/** Structural guard for a match.suggestion frame (defence-in-depth; the matcher
 *  already lexicon-validates what it sends). */
export function isMatchSuggestion(v: unknown): v is MatchSuggestion {
  if (v === null || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.subject === "string" && o.subject.length > 0 &&
    typeof o.need === "string" && NEED_TYPES.has(o.need) &&
    typeof o.urgency === "number" &&
    typeof o.rank === "number" &&
    Array.isArray(o.cells) && o.cells.length > 0 && o.cells.every((c) => typeof c === "string") &&
    typeof o.updatedAt === "string"
  );
}

export interface MatchStreamHandlers {
  onMatch: (m: MatchSuggestion) => void;
  signal?: AbortSignal;
}

export class MatcherClient {
  constructor(private readonly opts: MatcherClientOptions) {}

  private get fetch(): typeof fetch {
    return this.opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Register (or refresh) helper availability — body is the signed envelope's DAG-CBOR bytes. */
  async postAvailability(bytes: Uint8Array): Promise<AvailabilityAck> {
    const res = await this.fetch(`${this.opts.baseUrl}/availability`, {
      method: "POST",
      headers: { "content-type": "application/octet-stream" },
      // Uint8Array is a valid BufferSource body; cast for TS 5.x typed-array friction.
      body: bytes as BodyInit,
    });
    if (!res.ok) throw new Error(`matcher /availability failed: ${res.status} ${res.statusText}`);
    return (await res.json()) as AvailabilityAck;
  }

  /** Open ONE per-helper match SSE connection. `authBytes` is a signed
   *  app.aid.match.subscribe envelope's DAG-CBOR. Resolves when the stream ends
   *  or the signal aborts; rejects on a non-ok response so the caller reconnects. */
  async streamMatches(authBytes: Uint8Array, handlers: MatchStreamHandlers): Promise<void> {
    const res = await this.fetch(`${this.opts.baseUrl}/matches/stream`, {
      method: "POST",
      headers: { "content-type": "application/octet-stream" },
      body: authBytes as BodyInit,
      signal: handlers.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`matcher /matches/stream failed: ${res.status} ${res.statusText}`);
    }
    for await (const frame of sseEvents(res.body)) {
      if (frame.event !== "match") continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(frame.data);
      } catch {
        continue;
      }
      if (isMatchSuggestion(parsed)) handlers.onMatch(parsed);
    }
  }
}
